import { useEffect, useMemo, useState } from 'react'

import type { GrapeMoney } from 'types'
import type { GetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansMetalTierItem as MetalTier } from 'utils/web_api.schemas'

import type { MedicalPlanDisplayItem } from '../../member_onboarding_v2/medicalPlanTypes'
import { STRATEGY_CONFIGS } from '../../member_onboarding_v2/smartBudgetUtils'
import type { BenefitsBundleStrategy, PlansForBenefitsBundle } from '../../member_onboarding_v2/types'

interface UseMedicalPlanStrategyParams {
  medicalPlans: MedicalPlanDisplayItem[]
  allowanceAmount: GrapeMoney
  minimumMetalTier: MetalTier
  budgetStrategy: BenefitsBundleStrategy
  setMedicalPlan: (plan: MedicalPlanDisplayItem | null) => void
}

export function useMedicalPlanStrategy ({
  medicalPlans,
  allowanceAmount,
  minimumMetalTier,
  budgetStrategy,
  setMedicalPlan,
}: UseMedicalPlanStrategyParams) {
  // Calculate smart budget data for all strategies (memoized)
  const smartBudgetData = useMemo<Record<BenefitsBundleStrategy, PlansForBenefitsBundle>>(() => {
    return {
      flexible: STRATEGY_CONFIGS.flexible.findPlans(medicalPlans, allowanceAmount, minimumMetalTier),
      balanced: STRATEGY_CONFIGS.balanced.findPlans(medicalPlans, allowanceAmount, minimumMetalTier),
      traditional: STRATEGY_CONFIGS.traditional.findPlans(medicalPlans, allowanceAmount, minimumMetalTier),
    }
  }, [medicalPlans, allowanceAmount, minimumMetalTier])

  // Track which plan user has manually selected for each strategy
  const [selectedPlansPerStrategy, setSelectedPlansPerStrategy] = useState<Record<BenefitsBundleStrategy, MedicalPlanDisplayItem | null>>({
    flexible: null,
    balanced: null,
    traditional: null,
  })

  // Validate and clear invalid selections when strategy or plans change
  useEffect(() => {
    setSelectedPlansPerStrategy((prev) => {
      const userSelectedPlan = prev[budgetStrategy]
      if (!userSelectedPlan) return prev

      const strategyData = smartBudgetData[budgetStrategy]
      const isStillValid = strategyData?.all_plans_for_strategy?.some(
        plan => plan.plan.id === userSelectedPlan.plan.id
      )

      // Clear selection if it's no longer valid for this strategy
      if (!isStillValid) {
        return {
          ...prev,
          [budgetStrategy]: null,
        }
      }
      return prev
    })
  }, [budgetStrategy, smartBudgetData])

  // Determine which medical plan to show
  const medicalPlanForStrategy = useMemo(() => {
    const strategyData = smartBudgetData[budgetStrategy]
    const userSelectedPlan = selectedPlansPerStrategy[budgetStrategy]

    // If user selected a plan and it's in the list, use it
    if (userSelectedPlan) {
      const foundPlan = strategyData?.all_plans_for_strategy?.find(
        plan => plan.plan.id === userSelectedPlan.plan.id
      )
      if (foundPlan) return foundPlan
    }

    // Fallback to recommended plan
    return strategyData?.recommended_plan
  }, [smartBudgetData, budgetStrategy, selectedPlansPerStrategy])

  // Update parent's medical plan state when the plan for strategy changes
  useEffect(() => {
    setMedicalPlan(medicalPlanForStrategy || null)
  }, [medicalPlanForStrategy])

  return {
    smartBudgetData,
    medicalPlanForStrategy,
    selectedPlansPerStrategy,
    setSelectedPlansPerStrategy,
  }
}
